import * as React from "react";
import logo from '../assets/logo.png';
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams, useNavigate } from "react-router-dom";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import getApiBaseUrl from "helpers/getApiUrl";
import { validateCoordinates } from "utils/validation";
import CustomSelect from "components/custom-select";
import PdfViewer from "components/PDFViewer";
import LazyPdfThumbnail from "components/LazyPdfThumbnail";
import { usePdfDocument } from "hooks/usePdfDocument";
import { Paggination } from "components/common/paggination";
import { ShowToast } from "utils/showToast";
import { TiTick } from "react-icons/ti";
import fetchDocumentDataService from "services/fetchDocumentDataService";
import SetTemplateCoordinatesService from "services/setTemplateCoordinatesService";
// import updateInstanceStatus from "services/updateInstanceStatus";
import { useState, useEffect } from "react";
import {
  CheckboxButtonContainer,
  DateButtonContainer,
  DateSignedButtonContainer,
  EmailBoxButtonContainer,
  SignatureButtonContainer,
  TextBoxButtonContainer,
  PicklistButtonContainer,
  SignatureCoordinatesContainer,
  CheckBoxCoordinatesContainer,
  DateCoordinatesContainer,
  TextBoxCoordinatesContainer,
  PicklistCoordinatesContainer,
  EmailBoxCoordinatesContainer,
} from "containers";
import SelectionModeContainer from "containers/selectionMode/selectionModeContainer";
import { setGeneralDocumentData } from "redux/slices/generalDocumentReducer";
import { setActiveSignatory } from "redux/slices/activeSignatoryReducer";
import { setActivePageNumber } from "redux/slices/activePageNumberReducer";
import { setActiveCoordinate } from "redux/slices/activeCoordinateReducer";
import { setSupportedFields } from "redux/slices/supportedFieldsReducer";
import { setSignatoryList } from "redux/slices/signatoryListReducer";
import { salesforceFieldsMetadataSlice, setSalesforceFieldsMetadata } from "redux/slices/salesforceFieldsMetadataReducer";
import { ToastTypeEnum } from "constants/toastTypeEnum";
import loadingGif from "images/loading-cropped.gif";
import SessionTimer from "components/session-timer";
import CustomModal from "components/common/custom-modal";
import Confirmation from "components/confirmation";

export default function Home() {
  const signatoryList = useSelector(
    (state) => state.signatoryList.signatoryList
  );

  const activeSignatory = useSelector(
    (state) => state.activeSignatory.activeSignatory
  );

  const activeCoordinate = useSelector(
    (state) => state.activeCoordinate.activeCoordinate
  );


  const activePageNumber = useSelector(
    (state) => state.activePageNumber.activePageNumber
  );

  const pageDimensions = useSelector(
    (state) => state.generalDocument?.pageDimensions
  );

  const isSelectionMode = useSelector(
    (state) => state.activeCoordinate.isSelectionMode
  );

  const signatureCoordinates = useSelector(
    (state) => state.signatureCoordinates.signatureCoordinates
  );

  const textBoxCoordinates = useSelector(
    (state) => state.textBoxCoordinates.textBoxCoordinates
  );

  const emailBoxCoordinates = useSelector(
    (state) => state.emailBoxCoordinatesReducer?.emailBoxCoordinates || []
  );

  const picklistCoordinates = useSelector(
    (state) => state.picklistCoordinates.picklistCoordinates
  );

  const dateCoordinates = useSelector(
    (state) => state.dateCoordinates.dateCoordinates
  );

  const checkBoxCoordinates = useSelector(
    (state) => state.checkBoxCoordinates.checkBoxCoordinates
  );

  const generalDocumentData = useSelector(
    (state) => state.generalDocumentData.generalDocumentData
  );

  const allCoordinates = React.useMemo(() => [
    ...signatureCoordinates,
    ...textBoxCoordinates,
    ...dateCoordinates,
    ...checkBoxCoordinates,
    ...emailBoxCoordinates,
    ...picklistCoordinates,
  ], [signatureCoordinates, textBoxCoordinates, dateCoordinates, checkBoxCoordinates, emailBoxCoordinates, picklistCoordinates]);

  const writebackFields = React.useMemo(() => allCoordinates.filter((coord) => coord.writeBack), [allCoordinates]);
  const updateFromSalesforceFields = React.useMemo(() => allCoordinates.filter((coord) => coord.updateFromSalesforce), [allCoordinates]);

  // ─── Picklist validation: check if active tool is a picklist with no options ─
  const isActivePicklistInvalid = React.useMemo(() => {
    if (!isSelectionMode || !activeCoordinate?.eleId) return false;
    const isPicklist = picklistCoordinates.some(c => c.eleId === activeCoordinate.eleId);
    if (!isPicklist) return false;
    const options = activeCoordinate.options;
    if (!options || options === "") return true;
    const opts = options.toString().split(",").filter(o => o.trim() !== "");
    return opts.length === 0;
  }, [isSelectionMode, activeCoordinate, picklistCoordinates]);

  // ─── Global picklist validation: check if ANY picklist tool has no options ─
  const hasAnyInvalidPicklist = React.useMemo(() => {
    return picklistCoordinates.some(coord => {
      const options = coord.options;
      if (!options || options === "") return true;
      const opts = options.toString().split(",").filter(o => o.trim() !== "");
      return opts.length === 0;
    });
  }, [picklistCoordinates]);

  // Create a mapping of fields from `writeback` or `updateFromSalesforce` and their corresponding `mappingField`
  const writebackFieldsData = React.useMemo(() => writebackFields.map((field) => ({
    mappingField: field.mappingField,
  })), [writebackFields]);

  const updateFromSalesforceFieldsData = React.useMemo(() => updateFromSalesforceFields.map((field) => ({
    mappingField: field.mappingField,
  })), [updateFromSalesforceFields]);


  const defaultCanvasScale = "1";
  const scaleForImages = "0.3";

  const [orgId, setOrgId] = React.useState("");

  const brandLogoUrl = React.useMemo(
  () =>
    `${getApiBaseUrl()}/custom-branding/fetchBrandLogo?orgId=${encodeURIComponent(orgId)}`,
  [orgId]
);
console.log("brandLogoUrl:", brandLogoUrl);
  const [scale, setScale] = React.useState(1.0);
  const [panningState, setPanningState] = React.useState({ disabled: false });
  const [isDefaultChanged, setIsDefaultChanged] = React.useState(false);
  const [loadingImages, setLoadingImages] = React.useState(false);
  const scrollContainer = React.useRef(null);

  // Scroll to the first invalid picklist tool and highlight it
  const scrollToInvalidPicklist = React.useCallback(() => {
    if (!activeCoordinate?.eleId) return;
    const toolElements = document.querySelectorAll('.drag-tool');
    for (const el of toolElements) {
      if (el.style && scrollContainer?.current) {
        scrollContainer.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.style.transition = 'box-shadow 0.3s';
        el.style.boxShadow = '0 0 12px 4px rgba(229, 62, 62, 0.6)';
        setTimeout(() => {
          el.style.boxShadow = '';
        }, 1500);
        break;
      }
    }
  }, [activeCoordinate]);
  const scrollImage = React.useRef(null);
  const documentContainer = React.useRef(null);
  const [searchParams] = useSearchParams();
  const [popup, setPopup] = useState(false)
  const [isPDFLoading, setIsPDFLoading] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isFetching, setIsFetching] = React.useState(true);
  const [isFileShared, setIsFileShared] = React.useState(false);
  const [isSessionEnded, setIsSessionEnded] = React.useState(false);
  const [isSessionEndedModalOpen, setIsSessionEndedModalOpen] = React.useState(false);
  const [isFinalCountdownActive, setIsFinalCountdownActive] = React.useState(false);
  const [finalCountDownTime, setFinalCountDownTime] = React.useState(0);
  const [finalCountDownTimeRemaining, setFinalCountDownTimeRemaining] = React.useState();

  const sessionTime = 3600 * 2; // 2 hours session
  const [salesforceFieldsData, setSalesforceFieldsData] = useState();

  const uuid = searchParams.get("uuid");
  const template_instance_uuid =
    searchParams.get("template_instance_uuid") ?? null;

  const [totalPage, settotalPage] = React.useState([]);
  const [documentBounding, setDocumentBounding] = React.useState({
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });
  const [unfocusDivStyle, setUnfocusDivStyle] = React.useState({
    height: 0,
    width: 0,
  });

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const getDocumentData = async (reqBody, iv, orgId, token) => {
    const { uuid, templateInstanceUUID } = reqBody;

    dispatch(
      setGeneralDocumentData({
        generalDocumentData: {
          templateUUID: uuid,
          documentUUID: templateInstanceUUID,
          iv: iv,
          token: token,
          orgId: orgId
        },
      })
    );

    try {
      const apiResult = await fetchDocumentDataService(
        reqBody
      );
      const data = apiResult.data;
      const resolvedOrgId = data.orgId || orgId;
      // const sobjectAPIName = apiResult.sobjectAPIName;
      const signatoryList = apiResult.signatoryList;

      // try {
      //   setSalesforceFieldsData({ apiName: sobjectAPIName, fields: data.sobjectFields });
      // } catch (e) {
      //   // Non-critical: Salesforce fields metadata failed to set
      // }
      // dispatch(
      //   setSalesforceFieldsMetadata({
      //     sobjectAPIName: sobjectAPIName,
      //     salesforceFieldsMetadata: data.sobjectFields,
      //   })
      // );
      setOrgId(resolvedOrgId);
      // signature flow will remain constant
      dispatch(
        setGeneralDocumentData({
          generalDocumentData: {
            orgId: resolvedOrgId,
            templateUUID: uuid,
            documentUUID: templateInstanceUUID,
            signatureFlow: data.signatureFlow,
            iv: iv,
            token: token,
          },
        })
      );
      // dispatch(setSupportedFields({ supportedFields: data.allFieldsData }));
      dispatch(setSupportedFields({ supportedFields: data.allEditorToolsData }));

      dispatch(setSignatoryList({ signatoryList: signatoryList }));
      dispatch(
        setActiveSignatory({
          activeSignatory: signatoryList[0],
        })
      );
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    } catch (error) {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);

      if (error && error.message && error.message.includes('Session has expired')) {
        navigate("/session-expired", { replace: true });
        return;
      }

    }
  };

  React.useEffect(() => {
    // setup function
    const uuid = searchParams.get("uuid");
    const uuidTemplateInstance = searchParams.get("template_instance_uuid");
    let iv = searchParams.get("iv");
    let token = searchParams.get("token");
    let orgId = searchParams.get("orgId");



    if (uuid == null && uuidTemplateInstance == null && (token == null || iv == null || orgId == null)) {
      navigate("/page-not-found", { replace: true });
      return;
    }

    iv = iv?.trim();
    token = token?.trim();
    orgId = orgId?.trim();

    const reqBody = { uuid, templateInstanceUUID: uuidTemplateInstance };
    getDocumentData(reqBody, iv, orgId, token);
  }, []);

  React.useEffect(() => {
    if (isSessionEnded) {
      navigate("/session-expired", { replace: true });
    }
  }, [isSessionEnded, navigate]);

  // ─── Auto-deselect tool when clicking outside any tool on the canvas ───────
  React.useEffect(() => {
    const container = scrollContainer.current;
    if (!container) return;

    const handleCanvasMouseDown = (e) => {
      // If the click target is inside a drag-tool, don't deselect
      // (tool selection is handled by each container's handleDragStart)
      const clickedTool = e.target.closest('.drag-tool');
      if (clickedTool) return;

      // Clicked on empty canvas / background — clear selection
      dispatch(
        setActiveCoordinate({ activeCoordinate: {}, isSelectionMode: false })
      );
    };

    container.addEventListener('mousedown', handleCanvasMouseDown);
    return () => {
      container.removeEventListener('mousedown', handleCanvasMouseDown);
    };
  }, [dispatch]);
  React.useEffect(() => {
    if (pageDimensions?.height && pageDimensions?.width) {
      setUnfocusDivStyle({
        height: `${pageDimensions.height}px`,
        width: `${pageDimensions.width}px`,
      });
      return;
    }

    let attempts = 0;

    const intervalStamp = setInterval(() => {
      attempts++;

      const canvas = document.getElementsByTagName("canvas")[0];
      if (canvas?.style.height) {
        const style = {
          height: canvas.style.height,
          width: canvas.style.width,
        };
        setUnfocusDivStyle(style);
        clearInterval(intervalStamp);
      } else if (canvas?.height && canvas?.width) {
        const style = {
          height: `${canvas.height}px`,
          width: `${canvas.width}px`,
        };
        setUnfocusDivStyle(style);
        clearInterval(intervalStamp);
      } else if (attempts >= 15) { // stop after ~30 seconds
        clearInterval(intervalStamp);
      }

    }, 2000);

    return () => {
      clearInterval(intervalStamp);
    };
  }, [pageDimensions]);

  const handleGlobalSignatoryChange = (selectedSignatory) => {
    dispatch(
      setActiveCoordinate({ activeCoordinate: {}, isSelectionMode: false })
    );
    dispatch(
      setActiveSignatory({
        activeSignatory: selectedSignatory,
      })
    );
  };

  function handleDragLeave(event) {
    const element = documentContainer.current;
    element.style.backgroundColor = "";
    element.style.opacity = 1;
  }

  function handleDragEnter(event) {
    var rect = documentContainer.current.getBoundingClientRect();

    setDocumentBounding({
      top: rect.top,
      right: rect.right,
      bottom: rect.bottom,
      left: rect.left,
    });

    const element = documentContainer.current;
    element.style.backgroundColor = "#000000";
    element.style.opacity = 0.1;
  }

  function handlePaggination(index) {
    if (index === activePageNumber) return;

    dispatch(setActivePageNumber({ activePageNumber: index }));
    resetSelectionMode();
    scrollContainer.current.scrollTop = 0;
    const element = scrollImage.current.children[index];
    if (element) {
      const containerRect = scrollImage.current.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();
      const offsetTop = elementRect.top - containerRect.top;
      scrollImage.current.scrollTop += offsetTop;
    }
  }

  const changeSignatoryIdToNull = (finalDataList) => {
    return finalDataList.map((data) => ({ ...data, signatory_id: null }));
  };

  const resetSelectionMode = () => {

    dispatch(
      setActiveCoordinate({ activeCoordinate: {}, isSelectionMode: false })
    );
  };

  const [confirmationPopup, setConfirmationPopup] = useState(false)

  const [agreeToTnC, setAgreeToTnC] = useState(false)

  const continueHandleContinue = async () => {

    let signatureCoordinatesTemp = signatureCoordinates
    signatureCoordinatesTemp = signatureCoordinatesTemp.map(coord => {

      return coord;
    });


    let finalCoordData = [
      ...signatureCoordinatesTemp,
      ...textBoxCoordinates,
      ...dateCoordinates,
      ...checkBoxCoordinates,
      ...emailBoxCoordinates,
      ...picklistCoordinates,
    ].map(({ writeBack, updateFromSalesforce, mappingField, ...rest }) => rest);

    const validation = validateCoordinates(finalCoordData, signatoryList, dispatch);

    if (!validation.isValidate) {
      setIsLoading(false);
      ShowToast(ToastTypeEnum.ERROR, validation.msg);
      return;
    }

    try {
      await SetTemplateCoordinatesService(
        finalCoordData,
        uuid,
        template_instance_uuid
      );
      ShowToast(ToastTypeEnum.SUCCESS, "Document Sent Successfully.");


      const signingCompletionData = {
        documentId: uuid,
        templateInstanceId: template_instance_uuid,
        signatureFlow: generalDocumentData.signatureFlow,
        signatoryCount: signatoryList.length,
        completedAt: new Date().toISOString(),
        status: 'completed'
      };

      sessionStorage.setItem('signingCompletionData', JSON.stringify(signingCompletionData));
      localStorage.setItem('lastSignedDocument', JSON.stringify(signingCompletionData));


      const message = {
        type: "CLOSE_MODAL",
        payload: {
          message: "Document sent for signature successfully.",
          data: signingCompletionData,
          status: "success",
        },
        messageFrom: "Erudite Docs",
        status: true,
        action:
          template_instance_uuid == null ? "Create Template" : "Create TI",
        signatureFlow: generalDocumentData.signatureFlow,
        uuid: uuid,
        template_instance_uuid: template_instance_uuid,
      };

      // const message = {
      //   messageFrom: "Erudite Docs",
      //   status: true,
      //   action:
      //     template_instance_uuid == null ? "Create Template" : "Create TI",
      // };

      if (template_instance_uuid != null) {
        message.signatureFlow = generalDocumentData.signatureFlow;
        message.uuid = uuid;
        message.template_instance_uuid = template_instance_uuid;
      }
      window.parent.postMessage(message, "*");
      setIsLoading(false);
      setConfirmationPopup(false)
      localStorage.clear()
    } catch (error) {
      setIsLoading(false);
      ShowToast(ToastTypeEnum.ERROR, error.message || "Something went wrong saving coordinates.");
      localStorage.setItem("unexpected-error", error.stack);
      window.location.href = "/unexpected-error";
    }
  };

  const handleContinue = async () => { 
    
    // Block if any picklist has no options
    if (hasAnyInvalidPicklist) {
      ShowToast(ToastTypeEnum.ERROR, "Complete all required configurations before sending. Add options to all picklist tools.");
      return;
    }

    setIsFetching(false);

    if (confirmationPopup === false) {
      let finalCoordData = [
        ...signatureCoordinates,
        ...textBoxCoordinates,
        ...dateCoordinates,
        ...checkBoxCoordinates,
        ...emailBoxCoordinates,
        ...picklistCoordinates,
      ];

      let picklistNames = [];
      picklistCoordinates.map((picklist) => {
        picklistNames.push(picklist.name);
      });

      const hasEmptyName = picklistNames.some(
        (name) => !name || name.trim() === ''
      );
      const hasDuplicatePicklistNames = new Set(picklistNames).size !== picklistNames.length;
      if (hasEmptyName) {
        ShowToast(ToastTypeEnum.ERROR, "PickList name is required! Check all PickList names");
        return;
      } else if (hasDuplicatePicklistNames) {
        ShowToast(ToastTypeEnum.ERROR, "Duplicate PickList names found! Each PickList name should be unique.");
        return;
      }

      const validation = validateCoordinates(finalCoordData, signatoryList, dispatch);

      if (!validation.isValidate) {
        setIsLoading(false);
        ShowToast(ToastTypeEnum.ERROR, validation.msg);
        return;
      }
      setConfirmationPopup(true); // Open confirmation popup 
    } else {
      setConfirmationPopup(false); // Close confirmation popup
    }
    handleAgreeToTnc();
  };

  const handleAgreeToTnc = () => {
    if (agreeToTnC === true) {
      setIsLoading(true);
      continueHandleContinue();
    }
  };

  const closePopup = () => {
    setConfirmationPopup(false);
  };

  const getContinueEventFromConfirmation = (evt) => {
    setConfirmationPopup(false);
    setAgreeToTnC(evt);
  };

  useEffect(() => {
    handleAgreeToTnc();
    console.log("confirmationPopup in use effect:", confirmationPopup);
  }, [agreeToTnC]);

  const handleSessionExpired = () => {
    setIsSessionEndedModalOpen(true);
  };

  const closeModal = () => {
    setIsSessionEndedModalOpen(false);
  };

  const clearSession = () => {
    setIsSessionEnded(true);
    localStorage.clear();
    navigate('/session-expired', { replace: true });
  };


  const modalChildren = <p style={{ padding: "20px" }}>Your editor access session will expire in 10 minutes. Please save your work before then, as you won't be able to save it after that time and risk losing all of your changes. The system will be reloaded automatically after 10 minutes.</p>;

  const [salesforceFields, setSalesforceFields] = useState({})

  const handleSalesforceFields = async (data) => {
    const dt = await data;
    setSalesforceFields(dt);
  };

  const [dateFieldList, setDateFieldList] = useState([])
  const [writebackFieldList, setWritebackFieldList] = useState([]);

  useEffect(() => {
    const temp = salesforceFieldsData;
    const apiNameToRemove = writebackFieldList?.map((item) => item.apiName);
    setSalesforceFieldsData({
      apiName: temp?.apiName,
      fields: temp?.fields.filter((item) => !apiNameToRemove.includes(item.apiName)),
    });
  }, [writebackFieldList]);

  useEffect(() => {

    dispatch(
      setSalesforceFieldsMetadata({
        sobjectAPIName: salesforceFieldsData?.apiName,
        salesforceFieldsMetadata: salesforceFieldsData?.fields,
      })
    );

  }, [salesforceFieldsData])

  const updateSalesforceData = async () => {
    const obj2 = salesforceFields;
    if (activeCoordinate.writeBack) {
      setWritebackFieldList(
        writebackFieldList.every((value) => value !== activeCoordinate.mappingField)
          ? [...writebackFieldList, activeCoordinate.mappingField]
          : writebackFieldList
      );
    }

    if (dateFieldList[0]) {
      const exists = dateFieldList.some((obj) => obj.apiName === obj2.apiName);
      if (!exists) {
        setDateFieldList([...dateFieldList, obj2]);
      }
    } else {
      setDateFieldList([obj2]);
    }
  };

  // ─── Build the PDF URL ────────────────────────────────────────────────────
  const [pdfUrl, setPdfUrl] = useState('');

  // useEffect(() => {
  //   try {
  //     const apiBaseUrl = getApiBaseUrl();
  //     const url =
  //       template_instance_uuid != null && template_instance_uuid.length > 0
  //         ? `${apiBaseUrl}/docSignRequestInstance/fetchpdf?uuid=${uuid}&uuid_template_instance=${template_instance_uuid}`
  //         : `${apiBaseUrl}/docSignRequestInstance/fetchpdf?uuid=${uuid}`;
  //     setPdfUrl(url);
  //   } catch (error) {
  //     window.location.href = '/unexpected-error';
  //   }
  // }, [uuid, template_instance_uuid]);

  useEffect(() => {
    try {
      const apiBaseUrl = getApiBaseUrl();
      const url =
        template_instance_uuid != null && template_instance_uuid.length > 0
          ? `${apiBaseUrl}/docSignRequestInstance/fetchpdf?id=${uuid}&uuid_template_instance=${template_instance_uuid}`
          : `${apiBaseUrl}/docSignRequestInstance/fetchpdf?id=${uuid}`;
      setPdfUrl(url);
    } catch (error) {
      window.location.href = '/unexpected-error';
    }
  }, [uuid, template_instance_uuid]);

  // ─── Shared PDF document via usePdfDocument ─────────────────────────────
  // This hook loads the PDF ONCE and exposes a lazy getPage(n) function.
  // All consumers (main viewer + every thumbnail) share this single document.
  // When pdfUrl is empty the hook is a no-op.
  const {
    getPage: getPdfPage,
    numPages: pdfNumPages,
    isLoading: isPdfDocLoading,
  } = usePdfDocument(pdfUrl);

  // Keep totalPage in sync with pdfNumPages from the shared document
  React.useEffect(() => {
    if (pdfNumPages > 0) {
      settotalPage(pdfNumPages);
      setLoadingImages(false);
    }
  }, [pdfNumPages]);



  return (
    <>
      {isSessionEndedModalOpen && !isFinalCountdownActive ? <CustomModal modalHeaderTitle={"Session Will Expire Soon"} children={modalChildren} closeModal={closeModal} /> : null}
      {isLoading || isPDFLoading ? (

        <div
          style={{
            position: "fixed",
            zIndex: 5,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#fff",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "100vw",
              flexDirection: "column",
              fontFamily: "sans-serif",
            }}
          >
            <img
              src={loadingGif}
              alt="Editor Loader"
              style={{ minWidth: "150px", maxWidth: "250px" }}
              width="40%"
            />
            <br />
            {isFetching ? <h4>Fetching...</h4> : <h4>Saving...</h4>}
          </div>
        </div>
      ) : null}

      {isFileShared ? (
        <div
          style={{
            position: "absolute",
            zIndex: 5,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#E8EFF5",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              width: "100vw",
              flexDirection: "column",
            }}
          >
            <img src="https://media3.giphy.com/media/v1.Y2lkPTc5MGI3NjExNTkxODQ2YzNhNGQ3MmY4YTkxN2ZiMzEzODAyMTA5OWI4Yzk3ZDk1OSZjdD1z/I5dflEG9U9haQ3fUUv/giphy.gif" />
          </div>
        </div>
      ) : (

        <div className="container">
          <div className="navbar">
            {/* style={isDefaultChanged?{'cursor':'not-allowed'}:{}} */}
            <div>
              {/* {isSelectionMode && 
            <div style={{position:'fixed',zIndex:'999'}}>
            <div className="backdrop-focus" >
              &nbsp;
            </div>
            <div className="backdrop-navbar">
              &nbsp;
            </div>
            </div>
            } */}

              <div className="nav-flex">
                <div>
                  {/* <img
              src={`${process.env.REACT_APP_API_URL}/api/admin/branding/fetchBrandLogo?orgId=${orgId}`}
              className="logo"
              id="amazon-logo"
              alt='Logo'
            /> */}
                  <img
                    src={brandLogoUrl}
                    className="logo"
                    id="amazon-logo"
                    alt="Logo"
                    onError={(event) => {
                      event.currentTarget.onerror = null;
                      event.currentTarget.src = logo;
                    }}
                  />
                </div>
                <div>

                {((!isSelectionMode && !loadingImages)) && (
                  <button
                    className={`continue-btn${hasAnyInvalidPicklist ? ' continue-btn-disabled' : ''}`}
                    title={hasAnyInvalidPicklist ? "Complete all required configurations before sending" : ""}
                    onClick={handleContinue}
                  >
                    {template_instance_uuid == null
                      ? "SAVE TEMPLATE"
                      : "SEND TO SIGN"}
                  </button>
                )}

                </div>
              </div>

            </div>
          </div>
          <div className="mobile-navbar">
            <div className="left-sidebar-mobile">
              {/* <div className="tools-title"></div> */}


              {/* <div className="tools-title">Fields</div> */}
              <div className="signer">

                <CustomSelect
                  value={activeSignatory}
                  name="signer"
                  id="signer"
                  options={signatoryList}
                  onChange={(e) => {
                    handleGlobalSignatoryChange(e);
                  }}
                ></CustomSelect>
              </div>

            </div>
            <div className="flex-nav-mobile">
              <SignatureButtonContainer documentBounding={documentBounding} />
              <TextBoxButtonContainer documentBounding={documentBounding} />
              <DateButtonContainer documentBounding={documentBounding} />
              <CheckboxButtonContainer documentBounding={documentBounding} />
              <EmailBoxButtonContainer documentBounding={documentBounding} />
              <PicklistButtonContainer documentBounding={documentBounding} />
            </div>
          </div>
          <div className="left-sidebar-desktop" >
            <div className="left-sidebar-desktop-inner" >
              <div className="tools-title">Signer</div>
              <CustomSelect
                value={activeSignatory}
                name="signer"
                options={signatoryList}
                onChange={(e) => {
                  handleGlobalSignatoryChange(e);
                }}
              ></CustomSelect>

              <div className="tools-title">Fields</div>
              <div className="flex-container">
                <SignatureButtonContainer documentBounding={documentBounding} />
                <TextBoxButtonContainer documentBounding={documentBounding} />
                <DateButtonContainer documentBounding={documentBounding} />
                <CheckboxButtonContainer documentBounding={documentBounding} />
                <EmailBoxButtonContainer documentBounding={documentBounding} />
                <PicklistButtonContainer documentBounding={documentBounding} />
              </div>

              {!isSessionEnded ?
                <SessionTimer tiuuid={template_instance_uuid} onSessionClear={clearSession} onSessionExpired={handleSessionExpired} />
                : null
              }

              {isFinalCountdownActive ?
                <SessionTimer tiuuid={template_instance_uuid} onSessionClear={() => { }} onSessionExpired={() => { }} />
                : null
              }
            </div>
          </div>
          {confirmationPopup && (
            <div className="confirmationDiv">
              <Confirmation
                closePopup={closePopup}
                getContinueEventFromConfirmation={getContinueEventFromConfirmation}
                writebackFields={writebackFieldsData} // Pass writebackFields as prop
                updateFields={updateFromSalesforceFieldsData} // Pass updateFromSalesforceFields as prop
              />
            </div>
          )}
          <div >
            <div  >

              <div  >
                <div

                  className="document"
                  id="center-img-div"
                  ref={scrollContainer}
                  onDragEnter={handleDragEnter}
                  onDragLeave={handleDragLeave}
                  onDragOver={(event) => {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = "move";
                    event.target.style.cursor = "copy";
                  }}
                  onDrop={(event) => {
                    event.preventDefault();
                    event.target.style.cursor = "auto";
                    handleDragLeave();
                  }}>
                  <TransformWrapper

                    defaultScale={1}
                    maxScale={2.5}
                    disablePan={true}
                    centerZoomedOut
                    disablePadding
                    wheel={{ disabled: true }}
                    panning={panningState}
                    doubleClick={{ disabled: true }}
                    scale={scale}
                  >
                    {({ zoomIn, zoomOut }) => (
                      <React.Fragment >
                        {!(isLoading || isPDFLoading) && !isSelectionMode ? (
                          <footer className="footer">
                            <Paggination

                              activePage={activePageNumber}
                              totalPage={totalPage}
                              handlePaggination={handlePaggination}
                            ></Paggination>
                          </footer>
                        ) : null}
                        <TransformComponent >
                          <SignatureCoordinatesContainer
                            scrollContainer={scrollContainer}
                          />
                          <TextBoxCoordinatesContainer
                            scrollContainer={scrollContainer}
                          />
                          <DateCoordinatesContainer
                            scrollContainer={scrollContainer}
                          />
                          <CheckBoxCoordinatesContainer
                            scrollContainer={scrollContainer}
                          />
                          <EmailBoxCoordinatesContainer
                            scrollContainer={scrollContainer}
                          />
                          <PicklistCoordinatesContainer
                            scrollContainer={scrollContainer}
                          />
                          <div

                            ref={documentContainer}
                            onClick={() => resetSelectionMode()}
                            style={{
                              position: "absolute",
                              top: 0,
                              bottom: 0,
                              left: 0,
                              right: 0,
                              zIndex: 1,
                              height: unfocusDivStyle.height || (pageDimensions?.height ? `${pageDimensions.height}px` : "100%"),
                              width: unfocusDivStyle.width || (pageDimensions?.width ? `${pageDimensions.width}px` : "100%"),
                            }}
                          ></div>
                          <div className="testing">
                            {/* Main viewer: uses the shared pdfDoc (getPage) */}
                            <PdfViewer
                              getPage={getPdfPage}
                              numPages={pdfNumPages}
                              activePage={activePageNumber}
                              setPDFLoading={setLoadingImages}
                              setPageCount={settotalPage}
                              scale={parseFloat(defaultCanvasScale) || 1}
                            />
                          </div>
                        </TransformComponent>
                      </React.Fragment>
                    )}
                  </TransformWrapper>
                </div>
              </div>
            </div>
          </div>
          <div
            className="right-sidebar-desktop"
            id="right-sidebar-div"
            style={{ position: "relative", paddingRight: "10px", overflowY: "scroll", overflowX: 'hidden' }}
          >
            {isSelectionMode && activeCoordinate && <SelectionModeContainer handleSalesforceFields={handleSalesforceFields} />

            }
            {isSelectionMode && (
              <button
                className={`done-btn${isActivePicklistInvalid ? ' done-btn-disabled' : ''}`}
                title={isActivePicklistInvalid ? "Complete all required configurations before finishing" : ""}
                onClick={() => {
                  if (isActivePicklistInvalid) {
                    scrollToInvalidPicklist();
                    return;
                  }
                  updateSalesforceData()
                  dispatch(
                    setActiveCoordinate({
                      activeCoordinate: {},
                      isSelectionMode: false,
                    })
                  );
                }}
              >
                <TiTick style={{ margin: '0 auto 0 auto', alignSelf: 'center' }} />
                Done
              </button>
            )}
            <div
              ref={scrollImage}
              style={isSelectionMode ? {
                overflowX: "hidden",
                height: "22vh",
                width: "inherit",
                zIndex: '-1'
              } : {
                overflowX: "hidden",
                height: "92vh",
                width: "inherit",
                zIndex: '0'
              }}
            >
              {/* Desktop sidebar thumbnails — rendered lazily via IntersectionObserver */}
              {!isSelectionMode && totalPage !== 0 &&
                Array.from(Array(totalPage), (_, i) => (
                  <div
                    className="flexbox-container paggination"
                    onClick={() => handlePaggination(i)}
                    key={i}
                  >
                    <div className="flexbox-item">
                      <div className="page-count">{i + 1}</div>
                      <div
                        className="page-img"
                        style={{
                          border:
                            i === activePageNumber
                              ? "2px solid #6e859b"
                              : "1px solid #b3b3b34d",
                        }}
                      >
                        {/* LazyPdfThumbnail: only renders when scrolled into view */}
                        <LazyPdfThumbnail
                          getPage={getPdfPage}
                          pageNumber={i + 1}
                          scale={0.3}
                          placeholderW={120}
                          placeholderH={pageDimensions?.width && pageDimensions?.height ? Math.round(120 * (pageDimensions.height / pageDimensions.width)) : 150}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>


          </div>
          <div className="right-sidebar-mobile">
            {isSelectionMode && activeCoordinate && <SelectionModeContainer handleSalesforceFields={handleSalesforceFields} />}
            {isSelectionMode && (
              <button
                className={`done-btn${isActivePicklistInvalid ? ' done-btn-disabled' : ''}`}
                title={isActivePicklistInvalid ? "Complete all required configurations before finishing" : ""}
                onClick={() => {
                  if (isActivePicklistInvalid) {
                    scrollToInvalidPicklist();
                    return;
                  }
                  updateSalesforceData()
                  dispatch(
                    setActiveCoordinate({
                      activeCoordinate: {},
                      isSelectionMode: false,
                    })
                  );
                }}
              >
                <TiTick />

              </button>
            )}
            <div
              ref={scrollImage}
              style={{
                position: "fixed",
                left: "0",
                right: "0",
                bottom: "0",
                zIndex: "999",
                width: "fit-content",
                height: "20vh",
                marginBottom: "50px"
              }}
            >
              {/* Mobile sidebar thumbnails — also lazy */}
              {totalPage !== 0 &&
                Array.from(Array(totalPage), (_, i) => (
                  <div
                    className="flexbox-container paggination"
                    onClick={() => handlePaggination(i)}
                    key={i}
                  >
                    <div className="flexbox-item">
                      <div className="page-count">{i + 1}</div>
                      <div
                        className="page-img"
                        style={{
                          width: "120px",
                          objectFit: "contain",
                          border:
                            i === activePageNumber
                              ? "2px solid #6e859b"
                              : "1px solid #b3b3b34d",
                        }}
                      >
                        <LazyPdfThumbnail
                          getPage={getPdfPage}
                          pageNumber={i + 1}
                          scale={0.2}
                          placeholderW={120}
                          placeholderH={pageDimensions?.width && pageDimensions?.height ? Math.round(120 * (pageDimensions.height / pageDimensions.width)) : 120}
                        />
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>

        </div>
      )}
    </>
  );
}
